/* import __COLOCATED_TEMPLATE__ from './groups-empty-state.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent-2 */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type PlaygroundApiService from 'embercom/services/playground-api';
import {
  ExtractionType,
  type QuestionGenerationUpdateEvent,
} from 'embercom/lib/fin-playground/question-generation';
import { TOPICS_EXPLORER_HELP_URL } from 'embercom/lib/fin-playground/topics';
import type RouterService from '@ember/routing/router-service';
import type IntlService from 'ember-intl/services/intl';
// @ts-expect-error
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';
import { type SafeString } from '@ember/template/-private/handlebars';

export default class GroupsEmptyState extends Component {
  @service declare playgroundApi: PlaygroundApiService;
  @service declare intercomEventService: $TSFixMe;
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare appService: $TSFixMe;
  @service declare realTimeEventService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;

  @tracked showUploadCSVModal = false;
  @tracked isAddingQuestions = false;
  @tracked showTopicSelectionModal = false;

  ExtractionType = ExtractionType;

  @action
  trackLearnMoreClick(event: Event) {
    let target = event.target as HTMLElement;
    if (target.getAttribute('data-track-learn-more') === 'true') {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'clicked',
        object: 'learn_more_insights_topics_link',
        context: 'playground',
        place: 'groups_empty_state_tooltip',
      });
    }
  }

  get learnMoreLinkHtml(): SafeString {
    return sanitizeHtml(
      `<a href="${TOPICS_EXPLORER_HELP_URL}" target="_blank" class="underline" data-track-learn-more="true">${this.intl.t('ai-agent.playground.generate-dropdown.learn-more')}</a>`,
    );
  }

  constructor(owner: unknown, args: {}) {
    super(owner, args);
    this.realTimeEventService.on(
      'FinPlaygroundQuestionGenerationUpdate',
      this,
      '_handleQuestionGenerationResponse',
    );
  }

  willDestroy() {
    super.willDestroy();
    this.realTimeEventService.off(
      'FinPlaygroundQuestionGenerationUpdate',
      this,
      '_handleQuestionGenerationResponse',
    );
  }

  get hasAvailableExtractions() {
    return (
      this.playgroundSummary?.questionGenerationSummary?.availableExtractions?.all ||
      this.playgroundSummary?.questionGenerationSummary?.availableExtractions?.lowCsat ||
      this.playgroundSummary?.questionGenerationSummary?.availableExtractions?.handedToTeammate
    );
  }

  get playgroundSummary() {
    return this.playgroundApi.playgroundSummary;
  }

  get questionLimit() {
    return this.playgroundSummary?.questionLimit ?? 50;
  }

  get routeToTransitionTo() {
    return this.appService.app.canUseStandalone
      ? 'apps.app.standalone.testing.test'
      : 'apps.app.automation.fin-ai-agent.testing.test';
  }

  get questionGenerationIsPossible(): boolean {
    return this.playgroundSummary?.questionGenerationSummary?.isPossible ?? false;
  }

  get shouldShowGenerateFromInbox() {
    return this.appService.app.canUseFinPlaygroundTopics || this.questionGenerationIsPossible;
  }

  get isGenerateFromInboxDisabled() {
    return (
      !this.questionGenerationIsPossible ||
      this.playgroundApi.hasDisconnectedZendeskOrSalesforceStandalone ||
      this.playgroundApi.hasUnsupportedStandalonePlatforms
    );
  }

  get generateFromInboxDisabledTooltip(): string {
    if (this.playgroundApi.hasUnsupportedStandalonePlatforms) {
      return this.intl.t(
        'ai-agent.playground.blank-state.generate-from-inbox.disabled-tooltip-unsupported',
      );
    }
    if (this.playgroundApi.hasDisconnectedZendeskOrSalesforceStandalone) {
      return this.intl.t(
        'ai-agent.playground.blank-state.generate-from-inbox.disabled-tooltip-no-sync',
      );
    }
    return this.intl.t('ai-agent.playground.blank-state.generate-from-inbox.disabled-tooltip');
  }

  get shouldShowGenerateDropdown() {
    return this.appService.app.canUseFinPlaygroundTopics && this.questionGenerationIsPossible;
  }

  getTopicNameById(topicId?: string) {
    if (!topicId) {
      return null;
    }

    // Find the topic within the question generation summary
    let topics = this.playgroundSummary?.questionGenerationSummary?.availableTopics ?? [];
    let selectedItem = topics.find((topic) => topic.value === topicId);
    return selectedItem?.text ?? null;
  }

  get generateDropdownOptions() {
    let hasTopics = this.playgroundSummary?.questionGenerationSummary?.hasTopics ?? false;

    return [
      {
        text: this.intl.t('ai-agent.playground.generate-dropdown.generate-from-all'),
        value: 'generate-from-all',
      },
      {
        text: this.intl.t('ai-agent.playground.generate-dropdown.by-topic'),
        value: 'by-topic',
        isDisabled: !hasTopics,
        showNewTag: true,
        tooltip: !hasTopics
          ? String(
              this.intl.t('ai-agent.playground.generate-dropdown.by-topic-disabled-tooltip', {
                learnMoreLinkHtml: this.learnMoreLinkHtml,
                htmlSafe: true,
              }),
            )
          : undefined,
        onTooltipClick: !hasTopics ? this.trackLearnMoreClick : undefined,
      },
    ];
  }

  @action
  trackDropdownOpen() {
    let hasTopics = this.playgroundSummary?.questionGenerationSummary?.hasTopics ?? false;

    // Track when "By topic" option becomes visible in dropdown
    this.intercomEventService.trackAnalyticsEvent({
      action: 'rendered',
      object: 'generate_by_topic_cta',
      context: 'playground',
      place: 'groups_empty_state_dropdown',
      is_enabled: hasTopics,
    });
  }

  @action
  _handleQuestionGenerationResponse(event: QuestionGenerationUpdateEvent) {
    this.playgroundApi.updateQuestionGenerationSummary(event);
  }

  @action
  async createPlaygroundGroupWithForm(list: string[]) {
    try {
      let createdGroupSummary = await this.playgroundApi.createPlaygroundGroup(
        this.intl.t('ai-agent.playground.default-group-names.manual'),
        [],
        undefined,
        undefined,
        list,
      );

      this.trackAddingNewQuestions('add_questions_form');

      this.router.transitionTo(this.routeToTransitionTo, createdGroupSummary.id, {
        queryParams: {
          // We can skip the summary load because we just created the group
          // so we know we have an up to date summary
          skipSummaryLoad: true,
        },
      });
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.playground.banners.creating-group.question-creation-error'),
      );
    }
  }

  @action
  async createPlaygroundGroupWithCSV(list: string[]) {
    let showExceedingQuestionLimitBanner = false;

    if (list.length > this.questionLimit) {
      showExceedingQuestionLimitBanner = true;
      list = list.slice(0, this.questionLimit);
    }

    try {
      let createdGroupSummary = await this.playgroundApi.createPlaygroundGroup(
        this.intl.t('ai-agent.playground.default-group-names.csv'),
        [],
        undefined,
        undefined,
        list,
      );

      this.trackAddingNewQuestions('upload_csv_modal');

      this.router.transitionTo(this.routeToTransitionTo, createdGroupSummary.id, {
        queryParams: {
          // We can skip the summary load because we just created the group
          // so we know we have an up to date summary
          skipSummaryLoad: true,
          showExceedingQuestionLimitBanner,
        },
      });
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.playground.banners.creating-group.question-creation-error'),
      );
    }
  }

  @action
  async createPlaygroundGroupViaQuestionGeneration(
    extractionType: ExtractionType,
    topicId?: string,
  ) {
    try {
      let groupName;

      // Use topic name if creating by topic, otherwise use default name
      if (topicId) {
        let topicName = this.getTopicNameById(topicId);
        groupName =
          topicName || this.intl.t('ai-agent.playground.default-group-names.question-generation');
      } else {
        groupName = this.intl.t('ai-agent.playground.default-group-names.question-generation');
      }

      let createdGroupSummary = await this.playgroundApi.createPlaygroundGroup(
        groupName,
        [],
        undefined,
        extractionType,
        undefined,
        topicId,
      );

      this.trackAddingNewQuestions('question_generation', { topic_id: topicId });

      this.router.transitionTo(this.routeToTransitionTo, createdGroupSummary.id, {
        queryParams: {
          // We can skip the summary load because we just created the group
          // so we know we have an up to date summary
          skipSummaryLoad: true,
        },
      });
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.playground.banners.creating-group.question-creation-error'),
      );
    }
  }

  @action
  openUploadCSVModal() {
    this.showUploadCSVModal = true;
  }

  @action
  showAddQuestionsManually() {
    this.isAddingQuestions = true;
  }

  trackAddingNewQuestions(source: string, additionalArgs?: Record<string, any>) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'create_group_button',
      place: 'playground',
      section: 'test',
      context: source,
      ...additionalArgs,
    });
  }

  @action
  selectAddQuestionDropdownOption(option: string) {
    switch (option) {
      case 'generate-questions':
        this.createPlaygroundGroupViaQuestionGeneration(ExtractionType.All);
        break;
      case 'upload-csv':
        this.showUploadCSVModal = true;
        break;
      case 'add-question':
        this.isAddingQuestions = true;
        break;
    }
  }

  @action
  handleGenerateDropdownSelection(value: string) {
    switch (value) {
      case 'generate-from-all':
        this.createPlaygroundGroupViaQuestionGeneration(ExtractionType.All);
        break;
      case 'by-topic':
        this.showTopicSelectionModal = true;
        break;
    }
  }

  @action
  closeTopicSelectionModal() {
    this.showTopicSelectionModal = false;
  }

  @action
  generateQuestionsWithTopic(topicId?: string) {
    this.showTopicSelectionModal = false;
    this.createPlaygroundGroupViaQuestionGeneration(ExtractionType.All, topicId);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Playground::GroupsEmptyState': typeof GroupsEmptyState;
    'ai-agent/playground/groups-empty-state': typeof GroupsEmptyState;
  }
}
